import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, TrendingUp, BarChart3, Euro, Calendar as CalIcon, Trophy, Sparkles, Coins } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ComposedChart, Line, Legend } from "recharts";
import api from "@/lib/api";

const fmtEuro = (v) =>
    new Intl.NumberFormat("nl-BE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v || 0);

const fmtEuro2 = (v) =>
    new Intl.NumberFormat("nl-BE", { style: "currency", currency: "EUR", maximumFractionDigits: 2 }).format(v || 0);

const TOOL_LABELS = {
    photo_retouch: "AI Foto Retouch",
    upscale: "Upscale",
    outline_blur: "Omlijnen & blur",
    video_tour: "AI Videotour",
    other: "Overig",
};

function ToolsTooltip({ active, payload }) {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    return (
        <div className="bg-[#FFFFFF] border border-[#E5A93D]/40 rounded-sm px-3 py-2 text-xs">
            <div className="font-bold text-zinc-950 mb-1">{d.label}</div>
            <div className="text-[#7C3AED]">{d.credits} credit{d.credits !== 1 ? "s" : ""} verbruikt ({d.jobs} job{d.jobs !== 1 ? "s" : ""})</div>
            <div className="text-[#E5A93D]">{fmtEuro2(d.revenue_eur)} verkocht</div>
            <div className="text-zinc-500">AI-kost {fmtEuro2(d.ai_cost_eur)}</div>
        </div>
    );
}

function StatCard({ icon: Icon, label, value, sub, accent = "#E5A93D", testid }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-[#FFFFFF] border border-zinc-100 rounded-md p-5 lg:p-6"
            data-testid={testid}
        >
            <div className="flex items-center gap-2 mb-3">
                <Icon className="h-4 w-4" style={{ color: accent }} />
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold">{label}</div>
            </div>
            <div className="font-display font-black text-3xl lg:text-4xl tracking-tighter" style={{ color: accent }}>{value}</div>
            {sub && <div className="text-xs text-zinc-500 mt-1.5">{sub}</div>}
        </motion.div>
    );
}

function ChartTooltip({ active, payload }) {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    return (
        <div className="bg-[#FFFFFF] border border-[#E5A93D]/40 rounded-sm px-3 py-2 text-xs">
            <div className="font-bold text-zinc-950 mb-1">{d.label}</div>
            <div className="text-[#E5A93D]">{fmtEuro(d.revenue)}</div>
            <div className="text-zinc-500">{d.count} project{d.count !== 1 ? "en" : ""}</div>
            {d.count > 0 && <div className="text-zinc-500 mt-1">Gem. {fmtEuro(d.avg)}</div>}
        </div>
    );
}

export default function AdminStats() {
    const [data, setData] = useState(null);
    const [months, setMonths] = useState(12);
    const [loading, setLoading] = useState(true);
    const [toolData, setToolData] = useState(null);
    const [toolDays, setToolDays] = useState(30);

    useEffect(() => {
        let mounted = true;
        api.get(`/admin/stats/tools?days=${toolDays}`)
            .then(({ data: d }) => mounted && setToolData(d))
            .catch(() => {});
        return () => { mounted = false; };
    }, [toolDays]);

    useEffect(() => {
        let mounted = true;
        setLoading(true);
        api.get(`/admin/stats/revenue?months=${months}`)
            .then(({ data: d }) => mounted && setData(d))
            .finally(() => mounted && setLoading(false));
        return () => { mounted = false; };
    }, [months]);

    if (loading || !data) {
        return (
            <div className="min-h-screen bg-[#FAFAFA] text-zinc-950 flex items-center justify-center">
                <div className="text-zinc-500">Statistieken laden...</div>
            </div>
        );
    }

    const { series, summary, top_products } = data;
    const peak = series.reduce((m, s) => Math.max(m, s.revenue), 0);

    return (
        <div className="min-h-screen bg-[#FAFAFA] text-zinc-950 font-body pb-20" data-testid="admin-stats-page">
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-10">
                <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-950 mb-6" data-testid="stats-back">
                    <ArrowLeft className="h-4 w-4" /> Terug naar overzicht
                </Link>

                <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-1">Boekhouding</div>
                        <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tighter">Omzet & rapportage.</h1>
                        <p className="text-sm text-zinc-500 mt-2 max-w-xl">
                            Overzicht van je omzet per maand, gemiddelde projectwaarde en best verkochte formules.
                            Concepten en geannuleerde projecten zijn niet meegeteld.
                        </p>
                    </div>
                    <div className="flex items-center gap-2 bg-[#FFFFFF] border border-zinc-100 rounded-sm p-1" data-testid="months-selector">
                        {[6, 12, 24].map((n) => (
                            <button
                                key={n}
                                onClick={() => setMonths(n)}
                                className={`text-xs px-3 py-1.5 rounded-sm transition ${months === n ? "bg-[#E5A93D] text-black font-bold" : "text-zinc-500 hover:text-black"}`}
                                data-testid={`months-${n}`}
                            >
                                {n}m
                            </button>
                        ))}
                    </div>
                </div>

                {/* Top stats */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
                    <StatCard
                        icon={Euro}
                        label={`Omzet ${summary.current_month_label} (excl. BTW)`}
                        value={fmtEuro(summary.revenue_this_month)}
                        sub={`${summary.count_this_month} project${summary.count_this_month !== 1 ? "en" : ""}`}
                        testid="stat-revenue-month"
                    />
                    <StatCard
                        icon={TrendingUp}
                        label={`Omzet ${summary.current_year} YTD (excl. BTW)`}
                        value={fmtEuro(summary.revenue_ytd)}
                        sub={`${summary.count_ytd} project${summary.count_ytd !== 1 ? "en" : ""}`}
                        testid="stat-revenue-ytd"
                    />
                    <StatCard
                        icon={BarChart3}
                        label="Gemiddelde projectwaarde (excl. BTW)"
                        value={fmtEuro(summary.avg_project_value)}
                        sub={`Op basis van ${summary.count_ytd} project${summary.count_ytd !== 1 ? "en" : ""} dit jaar`}
                        accent="#7C3AED"
                        testid="stat-avg-project"
                    />
                    <StatCard
                        icon={CalIcon}
                        label="Maand piek (excl. BTW)"
                        value={fmtEuro(peak)}
                        sub={series.find((s) => s.revenue === peak && peak > 0)?.label || "—"}
                        accent="#059669"
                        testid="stat-peak-month"
                    />
                </div>

                {/* Bar chart */}
                <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-[#FFFFFF] border border-zinc-100 rounded-md p-6 lg:p-8 mb-8"
                    data-testid="revenue-chart"
                >
                    <h2 className="font-display font-bold text-xl tracking-tight mb-1">Omzet per maand</h2>
                    <p className="text-xs text-zinc-500 mb-6">Bedragen exclusief BTW (21%). Hover over een balk voor details.</p>
                    <div className="h-72 lg:h-80" style={{ minWidth: 0 }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={series} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
                                <defs>
                                    <linearGradient id="goldBar" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#C8922C" stopOpacity={0.95} />
                                        <stop offset="100%" stopColor="#E5A93D" stopOpacity={0.7} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                                <XAxis dataKey="label" stroke="#888" fontSize={11} tickLine={false} axisLine={false} />
                                <YAxis stroke="#888" fontSize={11} tickFormatter={(v) => fmtEuro(v).replace("€\u00A0", "€")} tickLine={false} axisLine={false} />
                                <Tooltip cursor={{ fill: "rgba(229,169,61,0.06)" }} content={<ChartTooltip />} />
                                <Bar dataKey="revenue" fill="url(#goldBar)" radius={[3, 3, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </motion.div>

                {/* Top products */}
                {top_products.length > 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-[#FFFFFF] border border-zinc-100 rounded-md p-6 lg:p-8"
                        data-testid="top-products"
                    >
                        <div className="flex items-center gap-2 mb-5">
                            <Trophy className="h-4 w-4 text-[#E5A93D]" />
                            <h2 className="font-display font-bold text-xl tracking-tight">Best verkochte formules</h2>
                        </div>
                        <div className="space-y-3">
                            {top_products.map((p, i) => {
                                const max = top_products[0].count || 1;
                                const pct = Math.round((p.count / max) * 100);
                                return (
                                    <div key={p.product_id} className="grid grid-cols-[24px_1fr_60px] items-center gap-4" data-testid={`top-product-${i}`}>
                                        <div className={`text-xs font-bold ${i === 0 ? "text-[#E5A93D]" : "text-zinc-500"}`}>#{i + 1}</div>
                                        <div>
                                            <div className="text-sm text-zinc-950 mb-1.5">{p.name}</div>
                                            <div className="h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                                                <div className="h-full bg-gradient-to-r from-[#E5A93D] to-[#C8922C] rounded-full transition-all" style={{ width: `${pct}%` }} />
                                            </div>
                                        </div>
                                        <div className="text-right text-sm font-bold text-zinc-600">{p.count}×</div>
                                    </div>
                                );
                            })}
                        </div>
                    </motion.div>
                )}

                {/* ───── Tools-inkomsten ───── */}
                {toolData && (
                    <div className="mt-12" data-testid="tools-revenue-section">
                        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-1">AI Tools & Credits</div>
                                <h2 className="font-display font-black text-3xl lg:text-4xl tracking-tighter">Tools-inkomsten.</h2>
                                <p className="text-sm text-zinc-500 mt-2 max-w-xl">
                                    Verkochte creditpakketten (Stripe) tegenover het dagelijkse AI-verbruik en de AI-kosten.
                                </p>
                            </div>
                            <div className="flex items-center gap-2 bg-[#FFFFFF] border border-zinc-100 rounded-sm p-1" data-testid="tool-days-selector">
                                {[14, 30, 90].map((n) => (
                                    <button
                                        key={n}
                                        onClick={() => setToolDays(n)}
                                        className={`text-xs px-3 py-1.5 rounded-sm transition ${toolDays === n ? "bg-[#E5A93D] text-black font-bold" : "text-zinc-500 hover:text-black"}`}
                                        data-testid={`tool-days-${n}`}
                                    >
                                        {n}d
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                            <StatCard
                                icon={Euro}
                                label={`Credits verkocht (${toolData.days}d)`}
                                value={fmtEuro2(toolData.summary.revenue_eur)}
                                sub={`${toolData.summary.credits_sold} credits · ${toolData.summary.purchases} aankopen`}
                                testid="stat-tools-revenue"
                            />
                            <StatCard
                                icon={Sparkles}
                                label={`Verbruik (${toolData.days}d)`}
                                value={`${toolData.summary.credits_spent} cr.`}
                                sub={`${toolData.summary.jobs} AI-bewerkingen`}
                                accent="#7C3AED"
                                testid="stat-tools-usage"
                            />
                            <StatCard
                                icon={TrendingUp}
                                label={`Marge (${toolData.days}d)`}
                                value={fmtEuro2(toolData.summary.margin_eur)}
                                sub={`AI-kosten ${fmtEuro2(toolData.summary.ai_cost_eur)}`}
                                accent="#059669"
                                testid="stat-tools-margin"
                            />
                            <StatCard
                                icon={Coins}
                                label="Credits all-time"
                                value={fmtEuro2(toolData.summary.all_time_revenue_eur)}
                                sub={`${toolData.summary.all_time_credits_sold} credits · ${toolData.summary.all_time_purchases} aankopen`}
                                accent="#0EA5E9"
                                testid="stat-tools-alltime"
                            />
                        </div>

                        <motion.div
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="bg-[#FFFFFF] border border-zinc-100 rounded-md p-6 lg:p-8 mb-8"
                            data-testid="tools-daily-chart"
                        >
                            <h3 className="font-display font-bold text-xl tracking-tight mb-1">Dagelijks verbruik & verkoop</h3>
                            <p className="text-xs text-zinc-500 mb-6">Paarse balken = verbruikte credits per dag · gouden lijn = verkochte creditpakketten (€)</p>
                            <div className="h-64 lg:h-72" style={{ minWidth: 0 }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <ComposedChart data={toolData.daily} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                                        <CartesianGrid stroke="rgba(0,0,0,0.05)" vertical={false} />
                                        <XAxis dataKey="label" stroke="#888" fontSize={10} tickLine={false} axisLine={false} interval="preserveStartEnd" minTickGap={24} />
                                        <YAxis yAxisId="credits" stroke="#7C3AED" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                                        <YAxis yAxisId="eur" orientation="right" stroke="#C8922C" fontSize={11} tickFormatter={(v) => `€${v}`} tickLine={false} axisLine={false} />
                                        <Tooltip cursor={{ fill: "rgba(124,58,237,0.05)" }} content={<ToolsTooltip />} />
                                        <Legend wrapperStyle={{ fontSize: 11 }} />
                                        <Bar yAxisId="credits" dataKey="credits" name="Credits verbruikt" fill="#7C3AED" fillOpacity={0.75} radius={[2, 2, 0, 0]} />
                                        <Line yAxisId="eur" type="monotone" dataKey="revenue_eur" name="Verkoop €" stroke="#E5A93D" strokeWidth={2.5} dot={false} />
                                    </ComposedChart>
                                </ResponsiveContainer>
                            </div>
                        </motion.div>

                        {toolData.per_tool.length > 0 && (
                            <motion.div
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="bg-[#FFFFFF] border border-zinc-100 rounded-md p-6 lg:p-8"
                                data-testid="tools-per-tool"
                            >
                                <div className="flex items-center gap-2 mb-5">
                                    <Sparkles className="h-4 w-4 text-[#7C3AED]" />
                                    <h3 className="font-display font-bold text-xl tracking-tight">Verbruik per tool ({toolData.days}d)</h3>
                                </div>
                                <div className="space-y-3">
                                    {toolData.per_tool.map((p, i) => {
                                        const max = toolData.per_tool[0].credits || 1;
                                        const pct = Math.max(4, Math.round((p.credits / max) * 100));
                                        return (
                                            <div key={p.tool_key} className="grid grid-cols-[24px_1fr_150px] items-center gap-4" data-testid={`tool-usage-${p.tool_key}`}>
                                                <div className={`text-xs font-bold ${i === 0 ? "text-[#7C3AED]" : "text-zinc-500"}`}>#{i + 1}</div>
                                                <div>
                                                    <div className="text-sm text-zinc-950 mb-1.5">{TOOL_LABELS[p.tool_key] || p.tool_key}</div>
                                                    <div className="h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                                                        <div className="h-full bg-gradient-to-r from-[#7C3AED] to-[#A78BFA] rounded-full transition-all" style={{ width: `${pct}%` }} />
                                                    </div>
                                                </div>
                                                <div className="text-right text-xs font-bold text-zinc-600">
                                                    {p.credits} cr. · {p.jobs} jobs
                                                    <div className="text-[10px] font-normal text-zinc-400">AI-kost {fmtEuro2(p.ai_cost_eur)}</div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </motion.div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
